import { useEffect, useRef, useState } from 'react';
import { useLocalStorageState } from '../local-storage.hooks';
import { CheckpointTypeFilterType } from '@/entities/checkpoint';
import { VehicleCheckTypeLocalStorage } from '@/entities/vehicle-check-type';
import debounce from 'lodash/debounce';

interface VehicleCheckTypeFilterProps {
  setCurrentPage: (page: number) => void;
}

export const useVehicleCheckTypeFilter = ({
  setCurrentPage,
}: VehicleCheckTypeFilterProps) => {
  const [debouncedFilter, setDebouncedFilter] =
    useState<CheckpointTypeFilterType>({ key: '' });
  const filtersEmptyRef = useRef<boolean>(true);

  /**
   * === Filter section ===
   */
  const [key, setKey] = useLocalStorageState({
    key: VehicleCheckTypeLocalStorage.KEY,
    removeOnClose: true,
  });

  const [status, setStatus] = useLocalStorageState({
    key: VehicleCheckTypeLocalStorage.STATUS,
    removeOnClose: true,
  });

  const [type, setType] = useLocalStorageState({
    key: VehicleCheckTypeLocalStorage.TYPE,
    removeOnClose: true,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      key: key || '',
      status,
      type,
    }));

    filtersEmptyRef.current = !key && !status && !type;
  }, [key, status, type]);

  const debounceFilterUpdate = (value: string) => {
    setKey(value);
    setCurrentPage(1);
    debounce((value: string) => {
      setDebouncedFilter((prev) => ({ ...prev, key: value }));
    }, 1000);
  };

  const searchByStatus = (value: string) => {
    setStatus(value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, status: value }));
  };

  const searchByType = (value: string) => {
    setType(value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, type: value }));
  };

  const clearFilters = () => {
    setKey('');
    setStatus('');
    setType('');
    setCurrentPage(1);
  };
  /**
   * === END OF Filter section ===
   */

  return {
    debouncedFilter,
    filtersEmpty: filtersEmptyRef.current,
    clearFilters,
    key,
    status,
    type,
    debounceFilterUpdate,
    searchByStatus,
    searchByType,
  };
};
